import { CheckedState } from "@radix-ui/react-checkbox"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import {
    JobsAssignmentNotificationTopics,
    JobsNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function JobsAssignmentNotificationsForm() {
    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: jobsNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<JobsNotificationTopics>("jobs")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!jobsNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const handleToggleAllNotifications = (method: NotificationMethods, shouldEnableAllNotifications: CheckedState) => {
        if (method === "email") {
            setShouldDisableAllEmailNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllEmailNotifications(!!shouldEnableAllNotifications)
        } else {
            setShouldDisableAllSmsNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllSmsNotifications(!!shouldEnableAllNotifications)
        }
    }

    const modifyFieldset = (
        fieldset: JobsAssignmentNotificationTopics,
        method: NotificationMethods,
        value: boolean,
    ) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof JobsAssignmentNotificationTopics
            if (notificationKey.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return jobsNotificationPreference.assignment
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.assignment, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<
            JobsAssignmentNotificationTopics,
            JobsAssignmentNotificationTopics,
            Error,
            JobsAssignmentNotificationTopics
        >
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {({ form }) => {
                const formValues = form.getValues()

                const emailValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_email"))
                    .map(([, value]) => value)

                const smsValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_sms"))
                    .map(([, value]) => value)

                const isAllNotificationsEmailEnabled = emailValues.every(Boolean)
                const isAllNotificationsSmsEnabled = smsValues.every(Boolean)
                const isSomeNotificationsEmailEnabled = emailValues.some(Boolean)
                const isSomeNotificationsSmsEnabled = smsValues.some(Boolean)

                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty
                                label="Enable assignment notifications"
                                isLoading={isLoadingData}
                                isControlled={false}
                                isEmailIndeterminate={
                                    isSomeNotificationsEmailEnabled && !isAllNotificationsEmailEnabled
                                }
                                isEmailChecked={isAllNotificationsEmailEnabled}
                                onEmailCheckedChange={(checked) => handleToggleAllNotifications("email", checked)}
                                isSmsIndeterminate={isSomeNotificationsSmsEnabled && !isAllNotificationsSmsEnabled}
                                isSmsChecked={isAllNotificationsSmsEnabled}
                                onSmsCheckedChange={(checked) => handleToggleAllNotifications("sms", checked)}
                            />
                        </ListItemStack>
                        <ListItemStack>
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job assigned"
                                supportText="Notify me when I am assigned a job."
                                emailCheckboxName="enable_job_notification_job_assigned_email"
                                smsCheckboxName="enable_job_notification_job_assigned_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job unassigned"
                                supportText="Notify me when I am unassigned from a job."
                                emailCheckboxName="enable_job_notification_job_unassigned_email"
                                smsCheckboxName="enable_job_notification_job_unassigned_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job updated"
                                supportText="Notify me when someone updates a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_updated_email"
                                smsCheckboxName="enable_job_notification_job_updated_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job rescheduled"
                                supportText="Notify me when someone reschedules a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_rescheduled_email"
                                smsCheckboxName="enable_job_notification_job_rescheduled_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job unscheduled"
                                supportText="Notify me when someone unschedules a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_unscheduled_email"
                                smsCheckboxName="enable_job_notification_job_unscheduled_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job cancelled"
                                supportText="Notify me when someone cancels a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_cancelled_email"
                                smsCheckboxName="enable_job_notification_job_cancelled_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
